import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { OfflineQueueProvider } from "./contexts/OfflineQueueContext";
import ProtectedRoute from "./components/Auth/ProtectedRoute";
import LoginPage from "./components/Auth/LoginPage";
import UserProfile from "./components/pages/UserProfile";
import HelpPage from "./components/pages/HelpPage";
import ProfileLayout from "./components/layouts/ProfileLayout";
import ErrorBoundary from "./components/common/ErrorBoundary";
import OfflineIndicator from "./components/common/OfflineIndicator";
import OfflineStatusBar from "./components/common/OfflineStatusBar";

// System Admin Pages
import SystemAdminLayout from "./components/layouts/SystemAdminLayout";
import SystemAdminDashboard from "./components/pages/system-admin/Dashboard";
import StoreManagement from "./components/pages/system-admin/StoreManagement";
import Companies from "./components/pages/system-admin/Companies";
import Brands from "./components/pages/system-admin/Brands";
import Users from "./components/pages/system-admin/Users";
import Settings from "./components/pages/system-admin/Settings";
import Reports from "./components/pages/system-admin/Reports";

// Company Admin Pages
import CompanyAdminLayout from "./components/layouts/CompanyAdminLayout";
import CompanyAdminDashboard from "./components/pages/company-admin/Dashboard";
import BrandsManagement from "./components/CompanyAdmin/BrandsManagement";

// Brand Admin Pages
import BrandAdminLayout from "./components/layouts/BrandAdminLayout";
import BrandAdminDashboard from "./components/pages/brand-admin/Dashboard";
import StoresManagement from "./components/pages/brand-admin/Stores";

// Store Admin Pages
import StoreAdminLayout from "./components/layouts/StoreAdminLayout";
import StoreAdminDashboard from "./components/StoreAdmin/Dashboard";
import MenuManagement from "./components/StoreAdmin/MenuManagement";
import StaffManagement from "./components/StoreAdmin/StaffManagement";
import StoreReports from "./components/StoreAdmin/Reports";
import StoreSettings from "./components/StoreAdmin/Settings";
import PaymentSettings from "./components/StoreAdmin/PaymentSettings";
import TaxReceiptSettings from "./components/StoreAdmin/TaxReceiptSettings";
import InvoiceSettings from "./components/StoreAdmin/InvoiceSettings";
import EmoneySettings from "./components/StoreAdmin/EmoneySettings";
import CustomerSettings from "./components/StoreAdmin/CustomerSettings";
import InventoryManagement from "./components/StoreAdmin/InventoryManagement";
import TaxScheduleSettings from "./components/StoreAdmin/TaxScheduleSettings";
import ReceiptRulesSettings from "./components/StoreAdmin/ReceiptRulesSettings";
import ProductCsvImport from "./components/StoreAdmin/ProductCsvImport";
import QRCodeManagement from "./components/StoreAdmin/QRCodeManagement";
import OrderManagement from "./components/StoreAdmin/OrderManagement";
import SalesManagement from "./components/StoreAdmin/SalesManagement";
import KitchenPrinterSettings from "./components/StoreAdmin/KitchenPrinterSettings";
import ReceiptCustomization from "./components/StoreAdmin/ReceiptCustomization";
import PrinterManagement from "./components/StoreAdmin/PrinterManagement";
import AccountingIntegration from "./components/StoreAdmin/AccountingIntegration";

// Store Pages (Mobile Order)
import StoreMenu from "./components/Store/StoreMenu";
import OrderStatus from "./components/Store/OrderStatus";
import CustomerTipPage from "./components/Store/CustomerTipPage";

// POS Pages
import POSLayout from "./components/POS/POSLayout";
import POSMain from "./components/POS/POSMain";
import OrderHistory from "./components/POS/OrderHistory";
import Checkout from "./components/POS/Checkout";
import CashDrawerManager from "./components/POS/CashDrawerManager";

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <OfflineQueueProvider>
            <OfflineIndicator />
            <OfflineStatusBar />
            <Routes>
            {/* パブリックルート */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/store/:storeId/menu" element={<StoreMenu />} />
            <Route path="/store/:storeId/tips" element={<CustomerTipPage />} />
            <Route
              path="/store/:storeId/order-status/:orderId"
              element={<OrderStatus />}
            />
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* 公開ヘルプページ */}
            <Route path="/help" element={<HelpPage />} />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<UserProfile />} />
          </Route>

          {/* システム管理者ルート */}
          <Route
            path="/system-admin/*"
            element={
              <ProtectedRoute allowedRoles={["SYSTEM_ADMIN"]}>
                <SystemAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<SystemAdminDashboard />} />
            <Route path="companies" element={<Companies />} />
            <Route path="brands" element={<Brands />} />
            <Route path="stores" element={<StoreManagement />} />
            <Route path="users" element={<Users />} />
            <Route path="settings" element={<Settings />} />
            <Route path="reports" element={<Reports />} />
          </Route>

          {/* 会社管理者ルート */}
          <Route
            path="/company-admin/*"
            element={
              <ProtectedRoute allowedRoles={["COMPANY_ADMIN"]}>
                <CompanyAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<CompanyAdminDashboard />} />
            <Route path="brands" element={<BrandsManagement />} />
          </Route>

          {/* ブランド管理者ルート */}
          <Route
            path="/brand-admin/*"
            element={
              <ProtectedRoute allowedRoles={["BRAND_ADMIN"]}>
                <BrandAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<BrandAdminDashboard />} />
            <Route path="stores" element={<StoresManagement />} />
          </Route>

          {/* 店舗管理者ルート */}
          <Route
            path="/store-admin/:storeId/*"
            element={
              <ProtectedRoute allowedRoles={["STORE_ADMIN"]}>
                <StoreAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<StoreAdminDashboard />} />
            <Route path="menu" element={<MenuManagement />} />
            <Route path="staff" element={<StaffManagement />} />
            <Route path="inventory" element={<InventoryManagement />} />
            <Route path="reports" element={<StoreReports />} />
            <Route path="settings" element={<StoreSettings />} />
            <Route path="payment-settings" element={<PaymentSettings />} />
            <Route path="tax-receipt-settings" element={<TaxReceiptSettings />} />
            <Route path="invoice-settings" element={<InvoiceSettings />} />
            <Route path="emoney-settings" element={<EmoneySettings />} />
            <Route path="customers" element={<CustomerSettings />} />
            <Route path="tax-schedules" element={<TaxScheduleSettings />} />
            <Route path="receipt-rules" element={<ReceiptRulesSettings />} />
            <Route path="csv-import" element={<ProductCsvImport />} />
            <Route path="qr" element={<QRCodeManagement />} />
            <Route path="orders" element={<OrderManagement />} />
            <Route path="sales" element={<SalesManagement />} />
            <Route path="kitchen-printers" element={<KitchenPrinterSettings />} />
            <Route path="receipt-customization" element={<ReceiptCustomization />} />
            <Route path="printers" element={<PrinterManagement />} />
            <Route path="accounting" element={<AccountingIntegration />} />
          </Route>

          {/* POSルート */}
          <Route
            path="/pos/:storeId"
            element={
              <ProtectedRoute allowedRoles={["STORE_ADMIN", "STORE_STAFF"]}>
                <POSLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<POSMain />} />
            <Route path="history" element={<OrderHistory />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="cash-drawer" element={<CashDrawerManager />} />
          </Route>

            {/* 存在しないパスの場合はログインページにリダイレクト */}
            <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </OfflineQueueProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
